import {props} from '@stylexjs/stylex';
import type {CompiledStyles, InlineStyles, StyleXArray} from '@stylexjs/stylex';
import type {Intent} from '../intent';
import {createArrayIntentType, getIntents, isIntent} from '../intent';
import type {View} from '../view';
import {addClassName} from '../style';

type StaticStyleXStyles = {$$css: unknown; [key: string]: unknown};
type CompiledDynamicStyleXStyles = [
  StaticStyleXStyles,
  {
    [key: string]: unknown;
  } & {$$css?: never}, // Explicitly disallow the $$css property
];

export type StyleXStyles = StaticStyleXStyles | CompiledDynamicStyleXStyles;

type StyleXIntent = StyleXArray<
  | (null | undefined | CompiledStyles)
  | boolean
  | Readonly<[CompiledStyles, InlineStyles]>
> &
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Intent<any>;

export const stylexIntent = createArrayIntentType(
  'StyleXIntent',
  (decorate) =>
    (styles: StyleXStyles): StyleXIntent =>
      decorate(styles) as StyleXIntent,
);

function addStyle(
  props: {style?: React.CSSProperties},
  style: React.CSSProperties,
): void {
  props.style = Object.assign({}, props.style, style);
}

export function runStyleXIntents<Props>(
  revision: View.ViewRevision<Props>,
): void {
  // Gather and insert any style intents for the current component.
  const intents = getIntents(stylexIntent, revision);

  if (intents) {
    const {className, style} = props(...intents);

    if (className) {
      addClassName(revision.props as {className?: string}, className);
    }

    if (style) {
      addStyle(revision.props as {style?: React.CSSProperties}, style);
    }
  }
}

const isStaticCss = (css: unknown): css is StyleXStyles =>
  typeof css === 'object' && css !== null && '$$css' in css;

const isDynamicCss = (css: unknown | unknown[]): css is StyleXStyles => {
  if (!Array.isArray(css) || css.length !== 2 || css.some(isIntent)) {
    return false;
  }

  const [declaration, vars] = css;

  return isStaticCss(declaration) && !isStaticCss(vars);
};

export function isStyleXCss(css: unknown | unknown[]): css is StyleXStyles {
  return isStaticCss(css) || isDynamicCss(css);
}
